import { randomRoomCode, randomSeed } from './codes';
import type { LobbyApi } from './lobbyApi';
import { parseRoom, parseRoomList, type OnlineRoom } from './parse';
import { onlineError } from './service';
import { visitorToken, type Visitor } from './visitor';

/** A match-server answer that was not OK. `raw` is the server's own text. */
export class VisitorLobbyError extends Error {
  constructor(
    readonly status: number,
    readonly raw: string,
  ) {
    super(onlineError(raw));
  }
}

/**
 * Lobby for a visitor, through the match server's `/visitor/*` routes.
 * No realtime: the lobby screen already polls its room every second.
 */
export class VisitorLobby implements LobbyApi {
  constructor(
    private readonly baseUrl: string,
    private readonly visitor: Visitor,
  ) {}

  async accessToken(): Promise<string | null> {
    return visitorToken(this.visitor);
  }

  async listOpenRooms(): Promise<OnlineRoom[]> {
    return parseRoomList(await this.request('GET', '/visitor/rooms'));
  }

  async createRoom(): Promise<OnlineRoom> {
    let last = 'Could not create a room.';
    for (let attempt = 0; attempt < 4; attempt += 1) {
      try {
        const room = parseRoom(
          await this.request('POST', '/visitor/rooms', {
            name: this.visitor.name,
            code: randomRoomCode(),
            seed: randomSeed(),
          }),
        );
        if (!room) throw new Error('Room could not be created.');
        return room;
      } catch (error) {
        if (!(error instanceof VisitorLobbyError)) throw error;
        last = error.message;
        if (!/duplicate|unique|code/i.test(error.raw)) break;
      }
    }
    throw new Error(last);
  }

  async joinRoom(roomId: string): Promise<OnlineRoom> {
    const room = parseRoom(
      await this.request('POST', '/visitor/join', { roomId, name: this.visitor.name }),
    );
    if (!room) throw new Error('Room could not be joined.');
    return room;
  }

  async startRoom(roomId: string): Promise<OnlineRoom> {
    const room = parseRoom(await this.request('POST', '/visitor/start', { roomId }));
    if (!room) throw new Error('Room could not start.');
    return room;
  }

  async leaveRoom(roomId: string): Promise<void> {
    await this.request('POST', '/visitor/leave', { roomId });
  }

  roomByCode(code: string): Promise<OnlineRoom | null> {
    return this.maybeRoom(`/visitor/room?code=${encodeURIComponent(code)}`);
  }

  roomById(roomId: string): Promise<OnlineRoom | null> {
    return this.maybeRoom(`/visitor/room?id=${encodeURIComponent(roomId)}`);
  }

  /** The match server knows the visitor from the token. */
  activePlayingRoom(): Promise<OnlineRoom | null> {
    return this.maybeRoom('/visitor/active');
  }

  watchRoom(): () => void {
    return () => {};
  }

  private async maybeRoom(path: string): Promise<OnlineRoom | null> {
    try {
      return parseRoom(await this.request('GET', path));
    } catch (error) {
      if (error instanceof VisitorLobbyError && error.status === 404) return null;
      throw error;
    }
  }

  private async request(method: 'GET' | 'POST', path: string, body?: unknown): Promise<unknown> {
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl.replace(/\/$/, '')}${path}`, {
        method,
        headers: {
          Authorization: `Bearer ${visitorToken(this.visitor)}`,
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch {
      throw new Error('Cannot reach the match server.');
    }
    if (response.status === 204) return null;
    const payload = (await response.json().catch(() => null)) as { error?: unknown } | null;
    if (response.ok) return payload;
    const raw =
      typeof payload?.error === 'string' && payload.error.trim()
        ? payload.error
        : `The match server answered ${response.status}.`;
    throw new VisitorLobbyError(response.status, raw);
  }
}
